import { Component } from '@theme/component';

/**
 * Accessible tabs for the Arvana feature tabs section.
 * Markup is rendered by sections/arvana-feature-tabs.liquid with the first panel visible and the
 * others `hidden`, so the section reads correctly before this module loads.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs - The `role="tab"` buttons, in order.
 * @property {HTMLElement[]} panels - The `role="tabpanel"` elements, in the same order.
 *
 * @extends {Component<Refs>}
 */
class ArvanaTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    // Theme editor: show the panel of the block being edited
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Click handler for a tab button (`on:click="/select"`).
   * @param {MouseEvent} event
   */
  select(event) {
    const index = this.refs.tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (index === -1) return;

    this.activate(index);
  }

  /** Previous/next buttons under the panel on mobile (`on:click="/previous"`, `"/next"`). */
  previous() {
    this.#step(-1);
  }

  next() {
    this.#step(1);
  }

  /** @param {number} delta */
  #step(delta) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.activate((Math.max(current, 0) + delta + tabs.length) % tabs.length);
  }

  /**
   * Arrow/Home/End navigation within the tab list (`on:keydown="/navigate"`).
   * Follows the WAI-ARIA tabs pattern with automatic activation.
   * @param {KeyboardEvent} event
   */
  navigate(event) {
    const { tabs } = this.refs;
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let next;
    switch (event.key) {
      case forward:
        next = (current + 1) % tabs.length;
        break;
      case backward:
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(next, { focus: true });
  }

  /**
   * Shows the panel at `index` and updates tab state.
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  activate(index, { focus = false } = {}) {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    const tab = tabs[index];
    if (!tab) return;

    if (focus) tab.focus({ preventScroll: true });

    // Keep the active tab visible when the tab list scrolls horizontally (mobile)
    const list = tab.parentElement;
    if (list && list.scrollWidth > list.clientWidth) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      list.scrollTo({
        left: tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
  }

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const index = this.refs.panels.findIndex((panel) => panel.contains(/** @type {Node} */ (event.target)));
    if (index !== -1) this.activate(index);
  };
}

if (!customElements.get('arvana-tabs-component')) {
  customElements.define('arvana-tabs-component', ArvanaTabsComponent);
}
